import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription, asapScheduler } from 'rxjs';
import { observeOn, take } from 'rxjs/operators';
import { SaasTransportCompany } from 'src/app/core/interfaces/saasTransportCompany';
import { SaasTransportCompanyService } from 'src/app/core/services/saas-transport-company.service';

/**
 * Pastilla que indica con qué empresa de transporte trabaja la vista y permite
 * cambiarla (o solo la muestra, con readonly). La selección es local a la vista
 * que la monta: arranca con initialNit (si está en la lista) o con la primera
 * empresa, y la emite para
 * que la vista haga su primera consulta. Cuando la SaaS no tiene empresas de
 * transporte (o el listado falla) emite null y no se pinta, para que la vista
 * consulte sin transportNit como antes.
 */
@Component({
  selector: 'app-transport-company-context',
  templateUrl: './transport-company-context.component.html',
  styleUrls: ['./transport-company-context.component.scss']
})
export class TransportCompanyContextComponent implements OnInit, OnDestroy {
  @Input() initialNit: string;
  @Input() readonly = false;
  // field: mismo alto y letra que los mat-form-field outline de una fila de filtros (list-filter).
  @Input() size: 'default' | 'field' = 'default';
  @Output() companyChange: EventEmitter<string> = new EventEmitter();

  readonly SEARCH_THRESHOLD = 5;
  companies: SaasTransportCompany[] = [];
  selected: SaasTransportCompany = null;
  searchControl: FormControl = new FormControl('');
  private subscription: Subscription;

  constructor(private saasTransportCompanyService: SaasTransportCompanyService) { }

  ngOnInit(): void {
    // asapScheduler: el listado cacheado responde en el mismo ciclo y la vista
    // padre recibiría el evento en medio de su detección de cambios.
    this.subscription = this.saasTransportCompanyService.list()
      .pipe(take(1), observeOn(asapScheduler))
      .subscribe(
        (companies: SaasTransportCompany[]) => this.initSelection(companies || []),
        () => this.initSelection([])
      );
  }

  ngOnDestroy(): void {
    if (this.subscription) { this.subscription.unsubscribe(); }
  }

  get canChange(): boolean {
    return !this.readonly && this.companies.length > 1;
  }

  get showSearch(): boolean {
    return this.companies.length > this.SEARCH_THRESHOLD;
  }

  get filteredCompanies(): SaasTransportCompany[] {
    const query = (this.searchControl.value || '').toString().trim().toLowerCase();
    if (!query) { return this.companies; }
    return this.companies.filter(company => company && company.name && company.name.toLowerCase().includes(query));
  }

  select(company: SaasTransportCompany): void {
    if (!this.canChange || !company) { return; }
    if (this.selected && this.selected.companyId === company.companyId) { return; }
    this.selected = company;
    this.companyChange.emit(company.companyId);
  }

  initials(name: string): string {
    if (!name) { return ''; }
    return name
      .replace(/\b(S\s?A\s?S|S\s?A|LTDA)\b/gi, '')
      .trim()
      .split(/\s+/)
      .filter(word => !!word)
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  }

  private initSelection(companies: SaasTransportCompany[]): void {
    this.companies = companies;
    const initial = companies.find(company => company && company.companyId === this.initialNit) || null;
    // En readonly la empresa viene dada (p. ej. la de los servicios a facturar):
    // mostrar otra sería engañoso, así que sin coincidencia no se pinta.
    const fallback = this.readonly && this.initialNit ? null : companies[0] || null;
    this.selected = initial || fallback;
    this.companyChange.emit(this.selected ? this.selected.companyId : null);
  }
}
